import React, { useState, useEffect } from "react";
import {
  FaShieldAlt,
  FaQrcode,
  FaKey,
  FaCheck,
  FaArrowLeft,
  FaCopy,
  FaDownload,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  generate2FASecret,
  generateQRCode,
  validate2FASetup,
  save2FASecret,
  generateBackupCodes,
} from "../utils/twoFactorAuth";
import "./TwoFactorSetup.css";

const TwoFactorSetup = ({ userEmail, userUid, onSetupComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: QR, 2: Verify, 3: Backup Codes
  const [secretData, setSecretData] = useState(null);
  const [qrCodeData, setQrCodeData] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [codesDownloaded, setCodesDownloaded] = useState(false);

  // Generate 2FA secret and QR code on component mount
  useEffect(() => {
    const initializeSetup = async () => {
      try {
        setIsLoading(true);

        // Generate secret
        const secret = generate2FASecret(userEmail);
        setSecretData(secret);

        // Generate QR code
        const qrCode = await generateQRCode(secret.otpauth_url);
        setQrCodeData(qrCode);

        setIsLoading(false);
      } catch (error) {
        console.error("2FA setup initialization error:", error);
        setError("2FA kurulumu başlatılırken hata oluştu. QR kod olmadan da devam edebilirsiniz.");

        // Still create secretData even if QR code fails, so user can continue with manual entry
        const secret = generate2FASecret(userEmail);
        setSecretData(secret);

        setIsLoading(false);
      }
    };

    initializeSetup();
  }, [userEmail]);

  // Handle verification code input
  const handleVerificationChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only digits
    if (value.length <= 6) {
      setVerificationCode(value);
      setError("");
    }
  };

  // Verify setup
  const handleVerifySetup = async () => {
    if (verificationCode.length !== 6) {
      setError("Lütfen 6 haneli kodu girin.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const isValid = validate2FASetup(verificationCode, secretData.secret);

      if (isValid) {
        // Generate backup codes first
        const codes = generateBackupCodes();

        // Save 2FA secret to Firebase
        const saved = await save2FASecret(
          secretData.secret,
          userEmail,
          userUid,
          codes
        );

        if (saved) {
          setBackupCodes(codes);
          setCurrentStep(3);
        } else {
          setError("2FA ayarları kaydedilirken hata oluştu.");
        }
      } else {
        setError(
          "Geçersiz kod. Lütfen authenticator uygulamanızı kontrol edin."
        );
      }
    } catch (error) {
      console.error("2FA verification error:", error);
      setError("Doğrulama sırasında hata oluştu.");
    }

    setIsLoading(false);
  };

  // Copy manual entry key
  const copyManualKey = () => {
    navigator.clipboard.writeText(secretData.manual_entry_key);
    alert("Manuel giriş anahtarı kopyalandı!");
  };

  // Download backup codes
  const downloadBackupCodes = () => {
    const content = [
      "Almanya Danışmanlık - 2FA Yedek Kodları",
      "=".repeat(50),
      `Oluşturulma Tarihi: ${new Date().toLocaleString("tr-TR")}`,
      `Email: ${userEmail}`,
      "",
      "ÖNEMLİ UYARILAR:",
      "- Bu kodları güvenli bir yerde saklayın",
      "- Her kod sadece bir kez kullanılabilir",
      "- Authenticator uygulamanıza erişemediğinizde kullanın",
      "- Bu dosyayı güvenli bir yerde saklayın",
      "",
      "YEDEK KODLAR:",
      "=".repeat(20),
      ...backupCodes.map((code, index) => `${index + 1}. ${code}`),
      "",
      "Bu kodları kaybetmeyin! 2FA'ya erişiminizi kaybederseniz",
      "hesabınızı kurtarmak için bu kodları kullanabilirsiniz.",
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `2FA-Backup-Codes-${
      new Date().toISOString().split("T")[0]
    }.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setCodesDownloaded(true);
  };

  // Complete setup
  const handleCompleteSetup = () => {
    if (!codesDownloaded) {
      setError("Lütfen yedek kodlarınızı indirin.");
      return;
    }

    onSetupComplete();
  };

  if (isLoading && currentStep === 1) {
    return (
      <div className="two-factor-setup">
        <div className="setup-container">
          <div className="loading-setup">
            <FaShieldAlt className="loading-icon" />
            <h3>2FA Kurulumu Hazırlanıyor...</h3>
            <p>QR kod oluşturuluyor, lütfen bekleyin.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="two-factor-setup">
      <div className="setup-container">
        <div className="setup-header">
          <button className="back-button" onClick={onCancel}>
            <FaArrowLeft />
            Geri Dön
          </button>
          <h2>
            <FaShieldAlt className="header-icon" />
            İki Faktörlü Doğrulama Kurulumu
          </h2>
          <div className="step-indicator">
            <div className={`step ${currentStep >= 1 ? "active" : ""}`}>1</div>
            <div className={`step ${currentStep >= 2 ? "active" : ""}`}>2</div>
            <div className={`step ${currentStep >= 3 ? "active" : ""}`}>3</div>
          </div>
        </div>

        {/* Step 1: QR Code */}
        {currentStep === 1 && (
          <div className="setup-step">
            <div className="step-content">
              <h3>
                <FaQrcode className="step-icon" />
                Adım 1: Authenticator Uygulamasını Ayarlayın
              </h3>

              <div className="qr-section">
                <div className="qr-code-container">
                  {qrCodeData && (
                    <img
                      src={qrCodeData}
                      alt="2FA QR Code"
                      className="qr-code"
                    />
                  )}
                </div>

                <div className="qr-instructions">
                  <h4>Nasıl Kurulur?</h4>
                  <ol>
                    <li>
                      Authenticator uygulamanızı açın (Google Authenticator,
                      Authy, vb.)
                    </li>
                    <li>QR kod tarama seçeneğini seçin</li>
                    <li>Bu QR kodu tarayın</li>
                    <li>
                      Uygulamanızda "Almanya Danışmanlık" hesabı görünecek
                    </li>
                  </ol>
                </div>
              </div>

              <div className="manual-entry">
                <button
                  className="toggle-manual"
                  onClick={() => setShowManualEntry(!showManualEntry)}
                >
                  <FaKey />
                  {showManualEntry
                    ? "Manuel Girişi Gizle"
                    : "Manuel Giriş Anahtarı"}
                </button>

                {showManualEntry && secretData && (
                  <div className="manual-key-section">
                    <p>
                      QR kod tarayamıyorsanız, bu anahtarı manuel olarak girin:
                    </p>
                    <div className="manual-key">
                      <code>{secretData.manual_entry_key}</code>
                      <button className="copy-key" onClick={copyManualKey}>
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="step-actions">
                <button
                  className="continue-button"
                  onClick={() => setCurrentStep(2)}
                  disabled={!secretData || isLoading}
                  style={{
                    pointerEvents: (!secretData || isLoading) ? 'none' : 'auto',
                    cursor: (!secretData || isLoading) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isLoading ? 'QR Kod Hazırlanıyor...' : 'Devam Et'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Verification */}
        {currentStep === 2 && (
          <div className="setup-step">
            <div className="step-content">
              <h3>
                <FaCheck className="step-icon" />
                Adım 2: Kurulumu Doğrulayın
              </h3>

              <div className="verification-section">
                <p>Authenticator uygulamanızda görünen 6 haneli kodu girin:</p>

                <div className="code-input-container">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={handleVerificationChange}
                    placeholder="000000"
                    className="verification-input"
                    maxLength={6}
                    autoComplete="off"
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <FaExclamationTriangle />
                    <span>{error}</span>
                  </div>
                )}

                <div className="verification-info">
                  <p>
                    <FaExclamationTriangle className="warning-icon" />
                    Kod her 30 saniyede bir değişir. Yeni bir kod bekleyip
                    tekrar deneyin.
                  </p>
                </div>
              </div>

              <div className="step-actions">
                <button
                  className="back-step-button"
                  onClick={() => setCurrentStep(1)}
                >
                  Geri
                </button>
                <button
                  className="verify-button"
                  onClick={handleVerifySetup}
                  disabled={verificationCode.length !== 6 || isLoading}
                >
                  {isLoading ? "Doğrulanıyor..." : "Doğrula"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Backup Codes */}
        {currentStep === 3 && (
          <div className="setup-step">
            <div className="step-content">
              <h3>
                <FaKey className="step-icon" />
                Adım 3: Yedek Kodlarınızı Saklayın
              </h3>

              <div className="backup-codes-section">
                <div className="backup-warning">
                  <FaExclamationTriangle className="warning-icon" />
                  <div>
                    <h4>ÖNEMLİ!</h4>
                    <p>
                      Bu yedek kodları güvenli bir yerde saklayın. Authenticator
                      uygulamanıza erişiminizi kaybederseniz, bu kodlarla giriş
                      yapabilirsiniz.
                    </p>
                  </div>
                </div>

                <div className="backup-codes-grid">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="backup-code">
                      <span className="code-number">{index + 1}.</span>
                      <code className="code-value">{code}</code>
                    </div>
                  ))}
                </div>

                <div className="backup-actions">
                  <button
                    className="download-codes"
                    onClick={downloadBackupCodes}
                  >
                    <FaDownload />
                    Kodları İndir (.txt)
                  </button>

                  {codesDownloaded && (
                    <div className="download-success">
                      <FaCheck />
                      Yedek kodlar indirildi
                    </div>
                  )}
                </div>

                {error && (
                  <div className="error-message">
                    <FaExclamationTriangle />
                    <span>{error}</span>
                  </div>
                )}
              </div>

              <div className="step-actions">
                <button
                  className="complete-button"
                  onClick={handleCompleteSetup}
                  disabled={!codesDownloaded}
                >
                  <FaShieldAlt />
                  Kurulumu Tamamla
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSetup;
